import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowRight, RotateCcw, Play, Trophy, Target, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_LABELS = {
  beginner: { fr: 'Débutant', en: 'Beginner', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  intermediate: { fr: 'Intermédiaire', en: 'Intermediate', color: 'text-blue-600', bg: 'bg-blue-50' },
  advanced: { fr: 'Avancé', en: 'Advanced', color: 'text-purple-600', bg: 'bg-purple-50' },
};

export default function AdaptivePathWidget() {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/adaptive-path`, { headers: getAuthHeaders(), withCredentials: true });
        setData(res.data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [getAuthHeaders]);

  if (loading || !data) return null;

  const { next_action, program_progress, global_progress_pct, skill_level, total_modules, total_completed } = data;
  const skill = SKILL_LABELS[skill_level] || SKILL_LABELS.beginner;
  const isFr = language === 'fr';

  const actionIcons = { start: Play, continue: ArrowRight, review: RotateCcw, completed: Trophy };
  const ActionIcon = actionIcons[next_action?.type] || Target;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="adaptive-path-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
          <Target className="w-5 h-5 text-blue-600" />
          {isFr ? 'Votre Parcours' : 'Your Path'}
        </h3>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${skill.bg} ${skill.color}`} data-testid="skill-level-badge">
          {isFr ? skill.fr : skill.en}
        </span>
      </div>

      {/* Global Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span>{isFr ? 'Progression globale' : 'Global progress'}</span>
          <span className="font-medium text-slate-700" data-testid="global-progress-value">{total_completed}/{total_modules} ({global_progress_pct}%)</span>
        </div>
        <Progress value={global_progress_pct} className="h-2" data-testid="global-progress-bar" />
      </div>

      {/* Program Progress Bars */}
      <div className="space-y-2.5 mb-4">
        {program_progress.map((pp) => (
          <div key={pp.program_id} className="flex items-center gap-3" data-testid={`program-progress-${pp.program_id}`}>
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: pp.color }} />
            <span className="text-xs text-slate-600 w-16 truncate">{pp.duration}</span>
            <div className="flex-1">
              <Progress value={pp.completion_pct} className="h-1.5" />
            </div>
            <span className="text-xs text-slate-500 w-10 text-right">{pp.completion_pct}%</span>
            {pp.certified && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
          </div>
        ))}
      </div>

      {/* Next Action */}
      {next_action && next_action.type !== 'completed' && (
        <div
          className={`rounded-xl p-3.5 cursor-pointer transition-all hover:shadow-md ${
            next_action.type === 'review'
              ? 'bg-amber-50 border border-amber-200 hover:border-amber-300'
              : 'bg-blue-50 border border-blue-200 hover:border-blue-300'
          }`}
          onClick={() => navigate(`/course/${next_action.module_id}`)}
          data-testid="next-action-card"
        >
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              next_action.type === 'review' ? 'bg-amber-100' : 'bg-blue-100'
            }`}>
              {next_action.type === 'review' ? (
                <AlertTriangle className="w-4.5 h-4.5 text-amber-600" />
              ) : (
                <ActionIcon className="w-4.5 h-4.5 text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-500 mb-0.5">
                {isFr ? next_action.reason_fr : next_action.reason_en}
              </p>
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="next-action-title">
                {isFr ? next_action.module_title_fr : next_action.module_title_en}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-2.5" />
          </div>
        </div>
      )}

      {next_action?.type === 'completed' && (
        <div className="rounded-xl p-3.5 bg-emerald-50 border border-emerald-200 text-center" data-testid="path-completed">
          <Trophy className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-700">
            {isFr ? next_action.reason_fr : next_action.reason_en}
          </p>
        </div>
      )}
    </div>
  );
}
